import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Navigation Component
const Navigation = ({ currentPage, setCurrentPage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <svg className="h-10 w-10 text-blue-600 mr-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="text-2xl font-bold text-blue-600">DoctrNow</span>
            </div>
          </div>
          
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {['Home', 'AI Features', 'For Clinics', 'For Investors', 'For Patients', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => setCurrentPage(item.toLowerCase().replace(' ', '-'))}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === item.toLowerCase().replace(' ', '-')
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          
          <div className="md:hidden">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          <div className="hidden md:block">
            <button
              onClick={() => setCurrentPage('partner-with-us')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Partner With Us
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
              {['Home', 'AI Features', 'For Clinics', 'For Investors', 'For Patients', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    setCurrentPage(item.toLowerCase().replace(' ', '-'));
                    setMobileMenuOpen(false);
                  }}
                  className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left transition-colors ${
                    currentPage === item.toLowerCase().replace(' ', '-')
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Homepage Component
const Homepage = ({ setCurrentPage }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                DoctrNow – <span className="text-blue-600">Smarter, Faster,</span> AI-driven Healthcare
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Launching Soon in UAE
              </p>
              <p className="text-lg text-gray-700 mb-8">
                DoctrNow is a revolutionary teleconsultation platform connecting patients, doctors, and hospitals in the UAE. 
                Built with AI diagnostics, smart doctor-patient routing, symptom checker, and seamless consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setCurrentPage('for-patients')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors cursor-pointer"
                >
                  Join Waitlist
                </button>
                <button 
                  onClick={() => setCurrentPage('ai-features')}
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors cursor-pointer"
                >
                  Learn More
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b"
                alt="AI Medical Technology Interface"
                className="rounded-2xl shadow-2xl w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About DoctrNow Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">About DoctrNow</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Revolutionary teleconsultation platform designed for the UAE healthcare ecosystem
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI Technology</h3>
              <p className="text-gray-600">Advanced AI diagnostics and smart routing for optimal patient care</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Symptom Checker</h3>
              <p className="text-gray-600">Intelligent symptom analysis with NLP-powered recommendations</p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Matching</h3>
              <p className="text-gray-600">AI-based doctor matching based on condition and availability</p>
            </div>
          </div>
        </div>
      </section>

      {/* Launch Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Launch Timeline</h2>
            <p className="text-xl text-gray-600">Our roadmap to revolutionize healthcare in the UAE</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mb-4 text-xl font-bold">1</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Phase 1</h3>
              <p className="text-blue-600 font-medium mb-2">Teleconsultation Launch</p>
              <p className="text-gray-600">DHA licensed clinics integration with core teleconsultation features</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mb-4 text-xl font-bold">2</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Phase 2</h3>
              <p className="text-blue-600 font-medium mb-2">Insurance Integration</p>
              <p className="text-gray-600">Insurance co-pay and claims processing automation</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center mb-4 text-xl font-bold">3</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Phase 3</h3>
              <p className="text-blue-600 font-medium mb-2">Pharmacy & Delivery</p>
              <p className="text-gray-600">Prescription fulfillment with home delivery services</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why We're Different</h2>
            <p className="text-xl text-gray-600">Solving real healthcare challenges in the UAE</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Reduce Commuting', desc: 'Skip traffic and long drives to clinics' },
              { title: 'No Queues', desc: 'Eliminate waiting rooms and hospital queues' },
              { title: '24/7 AI Support', desc: 'Round-the-clock intelligent assistance' },
              { title: 'EHR Integration', desc: 'Seamless medical record management' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">✓</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Launch Cities */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">Launching Soon in Dubai, Abu Dhabi, Sharjah</h2>
          <p className="text-xl text-blue-100 mb-8">Be among the first to experience the future of healthcare</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setCurrentPage('for-patients')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg text-lg font-semibold transition-colors cursor-pointer"
            >
              Join Waitlist
            </button>
            <a href="mailto:jafeer@doctrnow.com" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold transition-colors">
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// AI Features Component
const AIFeatures = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">AI-Powered Healthcare</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Advanced artificial intelligence technology designed to revolutionize patient care and medical decision-making
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">AI Symptom Checker with NLP</h2>
            <p className="text-lg text-gray-700 mb-6">
              Our advanced Natural Language Processing system understands patient symptoms in multiple languages, 
              providing accurate preliminary assessments and directing patients to appropriate care levels.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3">✓</span>
                Multi-language symptom understanding
              </li>
              <li className="flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3">✓</span>
                Real-time severity assessment
              </li>
              <li className="flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3">✓</span>
                Immediate triage recommendations
              </li>
            </ul>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1578496779937-3815e442abed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxYLXJheSUyMGFuYWx5c2lzfGVufDB8fHxibHVlfDE3NDg4NDc3OTV8MA&ixlib=rb-4.1.0&q=85"
              alt="AI Medical Data Analysis - Advanced Diagnostic Technology"
              className="rounded-xl shadow-lg w-full h-80 object-cover"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="order-2 lg:order-1">
            <img
              src="https://images.unsplash.com/photo-1638202993928-7267aad84c31"
              alt="AI-Powered Doctor Consultation"
              className="rounded-xl shadow-lg w-full h-80 object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Smart Doctor Routing</h2>
            <p className="text-lg text-gray-700 mb-6">
              AI-powered matching system that connects patients with the most suitable healthcare providers 
              based on medical condition, doctor expertise, availability, and patient preferences.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3">✓</span>
                Specialty-based matching
              </li>
              <li className="flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3">✓</span>
                Real-time availability checking
              </li>
              <li className="flex items-center">
                <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3">✓</span>
                Patient preference optimization
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Predictive Health Analytics</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Health Predictions</h3>
              <p className="text-gray-600">AI analyzes patient data to predict potential health risks and recommend preventive care</p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Reminders</h3>
              <p className="text-gray-600">Automated follow-up scheduling and medication reminders based on treatment plans</p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Chronic Care Alerts</h3>
              <p className="text-gray-600">Continuous monitoring and intelligent alerts for patients with chronic conditions</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">HIPAA/GDPR Compliant</h2>
          <p className="text-xl text-blue-100 mb-6">
            Built with privacy and security at its core, ensuring complete compliance with international healthcare data protection standards
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <span className="bg-blue-500 px-4 py-2 rounded-lg">End-to-End Encryption</span>
            <span className="bg-blue-500 px-4 py-2 rounded-lg">Secure Data Storage</span>
            <span className="bg-blue-500 px-4 py-2 rounded-lg">Audit Trails</span>
            <span className="bg-blue-500 px-4 py-2 rounded-lg">Access Controls</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Clinic Onboarding Form Component
const ClinicOnboarding = () => {
  const [formData, setFormData] = useState({
    clinic_name: '',
    contact_person: '',
    num_doctors: '',
    specialties: '',
    email: '',
    phone: '',
    location: '',
    dha_license: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/clinics/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          num_doctors: parseInt(formData.num_doctors)
        })
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          clinic_name: '',
          contact_person: '',
          num_doctors: '',
          specialties: '',
          email: '',
          phone: '',
          location: '',
          dha_license: ''
        });
      } else {
        alert('Error submitting form. Please try again.');
      }
    } catch (error) {
      alert('Error submitting form. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-gray-600 mb-6">We'll be in touch soon to help your clinic join DoctrNow.</p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Bring Your Clinic Online with DoctrNow</h1>
            <p className="text-lg text-gray-600">Join our network of DHA-licensed healthcare providers</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Clinic/Hospital Name *</label>
              <input
                type="text"
                required
                value={formData.clinic_name}
                onChange={(e) => setFormData({...formData, clinic_name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person *</label>
              <input
                type="text"
                required
                value={formData.contact_person}
                onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number of Doctors *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.num_doctors}
                  onChange={(e) => setFormData({...formData, num_doctors: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialties Offered *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., General Medicine, Cardiology"
                  value={formData.specialties}
                  onChange={(e) => setFormData({...formData, specialties: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location/Address *</label>
              <textarea
                required
                rows="3"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">DHA License Number (Optional)</label>
              <input
                type="text"
                value={formData.dha_license}
                onChange={(e) => setFormData({...formData, dha_license: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              {loading ? 'Submitting...' : 'Submit Clinic Onboarding'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Investor Form Component
const InvestorForm = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    uae_location: '',
    investment_type: '',
    ticket_size: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/investors/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          full_name: '',
          company_name: '',
          email: '',
          phone: '',
          uae_location: '',
          investment_type: '',
          ticket_size: ''
        });
      } else {
        alert('Error submitting form. Please try again.');
      }
    } catch (error) {
      alert('Error submitting form. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-gray-600 mb-6">Our team will reach out shortly to discuss investment opportunities.</p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Invest in the Future of Healthcare</h1>
            <p className="text-lg text-gray-600">Join us in revolutionizing healthcare delivery in the UAE</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <input
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone (Optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">UAE Location *</label>
              <select
                required
                value={formData.uae_location}
                onChange={(e) => setFormData({...formData, uae_location: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Location</option>
                <option value="Dubai">Dubai</option>
                <option value="Abu Dhabi">Abu Dhabi</option>
                <option value="Sharjah">Sharjah</option>
                <option value="Ajman">Ajman</option>
                <option value="Fujairah">Fujairah</option>
                <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                <option value="Umm Al Quwain">Umm Al Quwain</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type of Investment *</label>
              <select
                required
                value={formData.investment_type}
                onChange={(e) => setFormData({...formData, investment_type: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Investment Type</option>
                <option value="Pre-Seed">Pre-Seed</option>
                <option value="Seed">Seed</option>
                <option value="Series A">Series A</option>
                <option value="Strategic">Strategic Partnership</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expected Ticket Size *</label>
              <select
                required
                value={formData.ticket_size}
                onChange={(e) => setFormData({...formData, ticket_size: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Ticket Size</option>
                <option value="$10K - $50K">$10K - $50K</option>
                <option value="$50K - $100K">$50K - $100K</option>
                <option value="$100K - $500K">$100K - $500K</option>
                <option value="$500K - $1M">$500K - $1M</option>
                <option value="$1M+">$1M+</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              {loading ? 'Submitting...' : 'Submit Investment Interest'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Patient Waitlist Form Component
const PatientWaitlist = () => {
  const [formData, setFormData] = useState({
    patient_name: '',
    email: '',
    phone: '',
    services_interest: '',
    current_app_dislikes: '',
    premium_payment: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/patients/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          patient_name: '',
          email: '',
          phone: '',
          services_interest: '',
          current_app_dislikes: '',
          premium_payment: ''
        });
      } else {
        alert('Error submitting form. Please try again.');
      }
    } catch (error) {
      alert('Error submitting form. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to the Waitlist!</h2>
            <p className="text-gray-600 mb-6">You're all set! We'll notify you as soon as DoctrNow launches in your area.</p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Want Early Access to DoctrNow?</h1>
            <p className="text-lg text-gray-600">Join our waitlist and be among the first to experience AI-powered healthcare</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Patient Name *</label>
              <input
                type="text"
                required
                value={formData.patient_name}
                onChange={(e) => setFormData({...formData, patient_name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone (Optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Services of Interest *</label>
              <select
                required
                value={formData.services_interest}
                onChange={(e) => setFormData({...formData, services_interest: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Services</option>
                <option value="General Consultation">General Consultation</option>
                <option value="Specialist Consultation">Specialist Consultation</option>
                <option value="AI Symptom Checker">AI Symptom Checker</option>
                <option value="Prescription Management">Prescription Management</option>
                <option value="Health Monitoring">Health Monitoring</option>
                <option value="All Services">All Services</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">What do you dislike about current telehealth apps? *</label>
              <textarea
                required
                rows="3"
                value={formData.current_app_dislikes}
                onChange={(e) => setFormData({...formData, current_app_dislikes: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tell us what frustrates you about existing healthcare apps..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Would you pay for premium features? *</label>
              <select
                required
                value={formData.premium_payment}
                onChange={(e) => setFormData({...formData, premium_payment: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Answer</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Depends">Depends on features</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              {loading ? 'Joining...' : 'Join Waitlist'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Contact Component
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({ name: '', email: '', message: '' });
      } else {
        alert('Error sending message. Please try again.');
      }
    } catch (error) {
      alert('Error sending message. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Message Sent!</h2>
            <p className="text-gray-600 mb-6">Thank you for contacting us. We'll get back to you soon.</p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Send Another Message
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600">Get in touch with the DoctrNow team</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  required
                  rows="5"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg text-lg font-semibold transition-colors"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Get in touch</h2>
            
            <div className="space-y-6">
              <div className="flex items-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.73a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Email</h3>
                  <p className="text-gray-600">jafeer@doctrnow.com</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                  <p className="text-gray-600">Dubai, UAE</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                  <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Response Time</h3>
                  <p className="text-gray-600">Within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick Connect</h3>
              <p className="text-gray-600 mb-4">For urgent inquiries, reach out directly via WhatsApp</p>
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">
                WhatsApp Us
              </button>
            </div>
          </div>
        </div>

        {/* Company Contact Information */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8" data-testid="contact-company-info">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
          <p className="text-lg font-semibold text-gray-900" data-testid="contact-legal-name">DoctrNow Healthcare LLC-FZ</p>
          <p className="text-gray-700 mt-2">For customer support and enquiries:</p>
          <p className="text-gray-700">Email: <a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></p>
          <div className="mt-4">
            <p className="text-gray-900 font-medium">Location:</p>
            <p className="text-gray-700">Dubai, United Arab Emirates</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// About Us Component
const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20" data-testid="about-us-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">About DoctrNow</h1>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
          <p className="text-lg text-gray-700" data-testid="about-us-legal-name">
            DoctrNow is operated by <span className="font-semibold text-gray-900">DoctrNow Healthcare LLC-FZ</span>, a UAE registered healthcare technology company headquartered in Dubai.
          </p>
          <p className="text-lg text-gray-700">
            Our mission is to connect patients with licensed healthcare providers through secure digital healthcare services, teleconsultation, and healthcare management solutions.
          </p>
          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Company</h2>
            <p className="text-gray-700">DoctrNow Healthcare LLC-FZ</p>
            <p className="text-gray-700">Dubai, United Arab Emirates</p>
            <p className="text-gray-700">Email: <a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Component
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-gray-600">Effective Date: 3 September 2026</p>
          <p className="text-gray-600">Last Updated: 3 September 2026</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-10 text-gray-700 leading-relaxed">
          <p className="mb-4" data-testid="privacy-legal-name">
            DoctrNow is operated by <span className="font-semibold text-gray-900">DoctrNow Healthcare LLC-FZ</span> ("DoctrNow", "we", "us", or "our").
          </p>
          <p className="mb-4">
            This Privacy Policy explains how we collect, use, store, disclose, and protect personal information when you use the DoctrNow website, mobile applications, teleconsultation services, and related digital healthcare services.
          </p>
          <p className="mb-4">
            We are committed to protecting personal and health information in accordance with applicable laws and healthcare regulations of the United Arab Emirates.
          </p>
          <p className="mb-2">For privacy-related questions or requests, please contact:</p>
          <p className="mb-1">Email: <a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></p>
          <p className="mb-4">Website: <a href="https://doctrnow.com" className="text-blue-600 hover:underline">https://doctrnow.com</a></p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">1. Information We May Collect</h2>
          <p className="mb-4">Depending on the services you use, DoctrNow may collect the following information.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Personal and Account Information</h3>
          <p className="mb-2">This may include:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Full name</li>
            <li>Date of birth</li>
            <li>Gender</li>
            <li>Mobile number</li>
            <li>Email address</li>
            <li>Address</li>
            <li>Emirates ID or other identification information, where required</li>
            <li>Account login and authentication information</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Health and Medical Information</h3>
          <p className="mb-2">When you use healthcare or teleconsultation services, we may collect information such as:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Symptoms and reason for consultation</li>
            <li>Medical history</li>
            <li>Existing medical conditions</li>
            <li>Allergies</li>
            <li>Current or previous medications</li>
            <li>Consultation information</li>
            <li>Clinical notes</li>
            <li>Prescriptions</li>
            <li>Laboratory or diagnostic reports</li>
            <li>Medical images or documents uploaded by you</li>
            <li>Referral information</li>
            <li>Other information required by the healthcare professional to provide appropriate care</li>
          </ul>
          <p className="mb-4">Health information is treated as sensitive and confidential information and is subject to additional protections under applicable UAE healthcare laws and regulations.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Teleconsultation Information</h3>
          <p className="mb-2">When you participate in a teleconsultation, we may process:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Appointment date and time</li>
            <li>Doctor and patient details</li>
            <li>Consultation status</li>
            <li>Messages exchanged as part of the healthcare service</li>
            <li>Audio/video communication necessary to conduct the consultation</li>
            <li>Technical information required to establish and maintain the consultation</li>
          </ul>
          <p className="mb-4">DoctrNow will not record an audio or video consultation without appropriate notice and consent where recording is enabled or legally required.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Payment and Transaction Information</h3>
          <p className="mb-2">If you make a payment through DoctrNow, we may process:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Transaction amount</li>
            <li>Payment status</li>
            <li>Transaction reference</li>
            <li>Refund information</li>
            <li>Billing details where required</li>
          </ul>
          <p className="mb-2">Payment-card information may be processed directly by an authorized third-party payment provider such as Stripe.</p>
          <p className="mb-4">DoctrNow does not need to store your complete payment-card number where the payment provider processes it directly.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Insurance Information</h3>
          <p className="mb-2">Where insurance functionality is available, we may process information necessary for:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Insurance eligibility verification</li>
            <li>Policy or membership verification</li>
            <li>Claims-related processes</li>
            <li>Copayment determination</li>
            <li>Healthcare service authorization</li>
          </ul>
          <p className="mb-4">Information will only be shared with insurers, TPAs, healthcare providers or other authorized parties where necessary for the requested service and permitted by applicable law.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Location Information</h3>
          <p className="mb-2">If you give permission to access your location, DoctrNow may use location information for features such as:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Finding healthcare providers</li>
            <li>Showing nearby clinics, laboratories or pharmacies</li>
            <li>Supporting healthcare delivery</li>
            <li>Address selection and map functionality</li>
          </ul>
          <p className="mb-2">You can disable location access through your device settings.</p>
          <p className="mb-4">Certain location-based features may then become unavailable.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Technical Information</h3>
          <p className="mb-2">We may automatically collect limited technical information including:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>IP address</li>
            <li>Device type</li>
            <li>Operating system</li>
            <li>App version</li>
            <li>Browser type</li>
            <li>Login and security information</li>
            <li>Crash and diagnostic information</li>
            <li>Application activity required for security, troubleshooting and performance monitoring</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">2. How We Use Your Information</h2>
          <p className="mb-2">We use personal information only for legitimate and permitted purposes, including to:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Create and manage your DoctrNow account</li>
            <li>Verify your identity</li>
            <li>Schedule and manage appointments</li>
            <li>Connect you with authorized healthcare professionals</li>
            <li>Provide teleconsultation services</li>
            <li>Support diagnosis, treatment and continuity of care</li>
            <li>Create and maintain appropriate medical records</li>
            <li>Generate and manage prescriptions where legally permitted</li>
            <li>Coordinate laboratory, pharmacy, diagnostic or referral services where requested</li>
            <li>Process payments and refunds</li>
            <li>Verify insurance eligibility where applicable</li>
            <li>Send appointment confirmations and service notifications</li>
            <li>Provide customer support</li>
            <li>Maintain security and prevent unauthorized access or fraud</li>
            <li>Monitor service reliability and technical performance</li>
            <li>Meet healthcare record-keeping requirements</li>
            <li>Meet regulatory, legal, audit and reporting obligations</li>
            <li>Respond to lawful requests from competent UAE authorities</li>
          </ul>
          <p className="mb-4">We will not use health information for unrelated advertising purposes.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">3. Consent for Telehealth Services</h2>
          <p className="mb-2">Before receiving telehealth services, patients may be required to provide appropriate electronic consent.</p>
          <p className="mb-2">The consent process may cover:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Nature of the teleconsultation</li>
            <li>Limitations of remote healthcare</li>
            <li>Collection and use of relevant medical information</li>
            <li>Electronic communications</li>
            <li>Data storage</li>
            <li>Sharing of information with healthcare professionals</li>
            <li>Referrals or transfer to another healthcare provider</li>
            <li>Recording, if applicable</li>
            <li>Patient rights and responsibilities</li>
          </ul>
          <p className="mb-4">Where the patient is legally a minor or cannot provide valid consent, consent must be obtained from an appropriate parent, guardian or legally authorized representative as required by applicable law.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">4. How We Share Information</h2>
          <p className="mb-2">DoctrNow does not sell personal or medical information.</p>
          <p className="mb-4">We may share information only where reasonably necessary with the following categories of recipients.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Healthcare Professionals and Healthcare Facilities</h3>
          <p className="mb-4">Relevant medical information may be made available to licensed doctors, healthcare professionals or healthcare facilities involved in your care.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Laboratories, Pharmacies and Diagnostic Providers</h3>
          <p className="mb-4">Where you request or authorize such services, necessary information may be shared with participating laboratories, pharmacies or diagnostic providers.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Insurance Providers and TPAs</h3>
          <p className="mb-4">Where insurance services are used, necessary information may be shared for eligibility, approval, reimbursement or claims administration.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Technology and Service Providers</h3>
          <p className="mb-2">We may use carefully selected providers for services such as:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Cloud infrastructure</li>
            <li>Secure video communications</li>
            <li>Authentication</li>
            <li>Email and SMS notifications</li>
            <li>Customer support</li>
            <li>Payment processing</li>
            <li>Maps and location services</li>
            <li>Application monitoring</li>
            <li>Cybersecurity</li>
          </ul>
          <p className="mb-4">These providers may access only the information necessary to perform their contracted services and are required to protect information in accordance with applicable contractual and legal requirements.</p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Regulatory and Government Authorities</h3>
          <p className="mb-4">Information may be disclosed when required or permitted by UAE law, including to competent healthcare regulators, judicial authorities or other government authorities.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">5. Health Data Storage and Data Location</h2>
          <p className="mb-2">DoctrNow handles health information in accordance with applicable UAE healthcare-data requirements.</p>
          <p className="mb-2">For healthcare services subject to UAE and Dubai healthcare requirements, health information will be maintained using secure UAE-based infrastructure unless storage or transfer outside the UAE is permitted under applicable law and any required regulatory approval has been obtained.</p>
          <p className="mb-4">Where a legally permitted transfer occurs, appropriate safeguards will be applied to protect the confidentiality and security of the information.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">6. How Long We Keep Information</h2>
          <p className="mb-2">We retain information only for as long as necessary for:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Providing healthcare services</li>
            <li>Maintaining medical records</li>
            <li>Meeting regulatory requirements</li>
            <li>Resolving disputes</li>
            <li>Preventing fraud</li>
            <li>Fulfilling contractual obligations</li>
            <li>Complying with applicable law</li>
          </ul>
          <p className="mb-2">Medical and healthcare records may be subject to mandatory retention periods that prevent immediate deletion.</p>
          <p className="mb-2">Where applicable, electronic healthcare information may be retained for the period required under UAE healthcare laws and regulations, including applicable requirements relating to retention following the patient's last healthcare procedure.</p>
          <p className="mb-4">Other non-clinical information will be deleted or anonymized when it is no longer reasonably required, subject to legal obligations.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">7. Your Privacy Rights</h2>
          <p className="mb-2">Subject to applicable UAE laws and healthcare record-retention requirements, you may have the right to:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Request access to personal information held about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request completion of incomplete information</li>
            <li>Obtain information regarding how your information is processed</li>
            <li>Request transfer of certain information where technically feasible</li>
            <li>Request restriction of certain processing</li>
            <li>Object to certain processing</li>
            <li>Withdraw consent where processing depends on consent</li>
            <li>Request deletion of eligible personal information</li>
            <li>Contact us regarding a privacy concern</li>
          </ul>
          <p className="mb-2">Deletion rights may be limited where healthcare, financial, regulatory or other records must legally be retained.</p>
          <p className="mb-2">To submit a request, contact:</p>
          <p className="mb-2"><a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></p>
          <p className="mb-4">We may need to verify your identity before completing a privacy request.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">8. Account Deletion</h2>
          <p className="mb-2">Users may request deletion of their DoctrNow account by:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Using the account-deletion functionality within the application, where available; or</li>
            <li>Emailing <a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></li>
          </ul>
          <p className="mb-2">Deleting your account does not necessarily mean that every medical record can be immediately erased.</p>
          <p className="mb-2">Health records, prescriptions, consultation documentation, transaction information or other records that DoctrNow or an associated healthcare provider is legally required to retain will continue to be securely maintained for the applicable mandatory retention period.</p>
          <p className="mb-4">Information not subject to such requirements will be deleted or anonymized within a reasonable period.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">9. Withdrawal of Consent</h2>
          <p className="mb-2">Where processing is based on your consent, you may withdraw that consent at any time by contacting us or using available application settings.</p>
          <p className="mb-2">Withdrawal will apply to future processing and will not invalidate processing that lawfully occurred before withdrawal.</p>
          <p className="mb-4">You may also refuse or discontinue a teleconsultation, subject to applicable healthcare requirements.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">10. Information Security</h2>
          <p className="mb-2">We use reasonable technical and organizational safeguards appropriate to the sensitivity of the information we process.</p>
          <p className="mb-2">These may include:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Encryption of information during transmission</li>
            <li>Encryption or equivalent protection for stored sensitive information</li>
            <li>Authentication and access controls</li>
            <li>Role-based access to health information</li>
            <li>Logging and monitoring</li>
            <li>Secure backups</li>
            <li>Security testing</li>
            <li>Access restrictions for employees and service providers</li>
            <li>Incident-management procedures</li>
          </ul>
          <p className="mb-4">Although we take appropriate measures to protect information, no internet-based system can guarantee absolute security.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">11. Healthcare Professionals' Access</h2>
          <p className="mb-2">Healthcare professionals may only access information reasonably necessary to provide healthcare services or perform authorized duties.</p>
          <p className="mb-2">Healthcare professionals remain responsible for maintaining patient confidentiality and complying with their applicable professional and regulatory obligations.</p>
          <p className="mb-4">Telehealth encounters and relevant clinical information may become part of the patient's medical record as required by applicable healthcare regulations.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">12. Children and Minors</h2>
          <p className="mb-2">DoctrNow does not knowingly allow a legally underage patient to independently provide consent where parental or guardian consent is legally required.</p>
          <p className="mb-2">Where healthcare services are provided to a minor, an appropriate parent, legal guardian or authorized representative may be required to:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Create or manage the patient's profile</li>
            <li>Provide required consent</li>
            <li>Participate in the consultation where appropriate</li>
            <li>Provide necessary healthcare information</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">13. Cookies and Website Technologies</h2>
          <p className="mb-2">Our website may use necessary cookies and similar technologies for:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Authentication</li>
            <li>Security</li>
            <li>Remembering user preferences</li>
            <li>Website operation</li>
            <li>Performance monitoring</li>
          </ul>
          <p className="mb-4">Where legally required, users will be provided with appropriate choices regarding non-essential cookies.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">14. Third-Party Services and Links</h2>
          <p className="mb-2">DoctrNow may integrate with third-party services such as payment providers, maps, healthcare providers, laboratories, pharmacies, insurers or other authorized service providers.</p>
          <p className="mb-2">Third-party services may operate under their own privacy policies when they act independently.</p>
          <p className="mb-4">DoctrNow evaluates integrations and seeks appropriate protections for personal and health information that is processed on our behalf.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">15. Emergency Medical Situations</h2>
          <p className="mb-2">DoctrNow is not an emergency-response service.</p>
          <p className="mb-2">If you believe you are experiencing a medical emergency, do not rely on the DoctrNow application or wait for an online consultation.</p>
          <p className="mb-4">Please contact the appropriate UAE emergency services or attend the nearest emergency department.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">16. Changes to This Privacy Policy</h2>
          <p className="mb-2">We may update this Privacy Policy when:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>Our services change</li>
            <li>New functionality is introduced</li>
            <li>Regulatory requirements change</li>
            <li>Our information-processing practices change</li>
          </ul>
          <p className="mb-2">The latest version will always be published at:</p>
          <p className="mb-2"><a href="https://doctrnow.com/privacy-policy" className="text-blue-600 hover:underline">https://doctrnow.com/privacy-policy</a></p>
          <p className="mb-2">The Effective Date or Last Updated date will be shown at the beginning of this Privacy Policy.</p>
          <p className="mb-4">Where a material change requires additional consent, we will obtain that consent as required by applicable law.</p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">17. Applicable UAE Requirements</h2>
          <p className="mb-2">DoctrNow's privacy and healthcare-data practices are designed to take account of applicable UAE requirements, including, where relevant:</p>
          <ul className="list-disc pl-6 space-y-1 mb-4">
            <li>UAE Federal Decree-Law No. 45 of 2021 concerning the Protection of Personal Data</li>
            <li>UAE Federal Law No. 2 of 2019 concerning the Use of Information and Communications Technology in Health Fields</li>
            <li>Applicable UAE medical-liability and healthcare regulations</li>
            <li>Applicable Dubai Health Authority telehealth standards and healthcare-data requirements</li>
            <li>Other applicable UAE federal and local healthcare and data-protection requirements</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">18. Contact Us</h2>
          <p className="mb-2">For privacy questions, requests to access or correct information, account-deletion requests, consent withdrawal or other privacy concerns, please contact:</p>
          <p className="font-semibold text-gray-900 mb-2">DoctrNow Healthcare LLC-FZ</p>
          <p className="mb-1">Email:</p>
          <p className="mb-3"><a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></p>
          <p className="mb-1">Website:</p>
          <p><a href="https://doctrnow.com" className="text-blue-600 hover:underline">https://doctrnow.com</a></p>
        </div>
      </div>
    </div>
  );
};

// Terms & Conditions Component
const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms &amp; Conditions</h1>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
          <p className="text-lg text-gray-700" data-testid="terms-legal-name">
            These services are provided by <span className="font-semibold text-gray-900">DoctrNow Healthcare LLC-FZ</span>, Dubai, United Arab Emirates.
          </p>
          <p className="text-lg text-gray-700">
            By accessing or using this website, users agree to comply with all applicable terms, conditions, and legal requirements governing the use of DoctrNow services.
          </p>
          <p className="text-lg text-gray-700">
            For questions regarding these terms, please contact:
            {' '}
            <a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Support Component
const Support = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20" data-testid="support-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Support</h1>
          <p className="text-xl text-gray-600">Need help with DoctrNow services?</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Email</h2>
            <p className="text-gray-700"><a href="mailto:admin@doctrnow.com" className="text-blue-600 hover:underline">admin@doctrnow.com</a></p>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Response Time</h2>
            <p className="text-gray-700">Within 1–2 business days</p>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Company</h2>
            <p className="text-gray-700" data-testid="support-legal-name">DoctrNow Healthcare LLC-FZ</p>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Location</h2>
            <p className="text-gray-700">Dubai, United Arab Emirates</p>
          </div>
        </div>
      </div>
    </div>
  );
};


// SEO-friendly document titles per route
const PAGE_TITLES = {
  '/home': 'Doctor Now',
  '/ai-features': 'AI Features | Doctor Now',
  '/for-clinics': 'For Clinics | Doctor Now',
  '/partner-with-us': 'Partner With Us | Doctor Now',
  '/for-investors': 'For Investors | Doctor Now',
  '/for-patients': 'For Patients | Doctor Now',
  '/contact': 'Contact | Doctor Now',
  '/support': 'Support | Doctor Now',
  '/about-us': 'About Us | Doctor Now',
  '/privacy-policy': 'Privacy Policy | Doctor Now',
  '/terms-and-conditions': 'Terms and Conditions | Doctor Now',
};

// Maps an internal page key to its route path
const pageToPath = (key) => (key === 'home' ? '/' : `/${key}`);

const DEFAULT_DESCRIPTION = 'Doctor Now - AI-powered teleconsultation platform for the UAE, connecting patients with licensed healthcare providers.';
const PAGE_DESCRIPTIONS = {
  '/privacy-policy': 'Read the DoctrNow Privacy Policy explaining how personal, medical and healthcare information is collected, used, protected and managed when using DoctrNow digital healthcare and teleconsultation services.',
};

// Layout with real client-side routing
function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname === '/' ? 'home' : location.pathname.slice(1);
  const setCurrentPage = (key) => navigate(pageToPath(key));

  useEffect(() => {
    const path = location.pathname === '/' ? '/home' : location.pathname;
    document.title = PAGE_TITLES[path] || 'Doctor Now';
    const metaEl = document.querySelector('meta[name="description"]');
    if (metaEl) metaEl.setAttribute('content', PAGE_DESCRIPTIONS[path] || DEFAULT_DESCRIPTION);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="App">
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <Routes>
        <Route path="/" element={<Homepage setCurrentPage={setCurrentPage} />} />
        <Route path="/home" element={<Homepage setCurrentPage={setCurrentPage} />} />
        <Route path="/ai-features" element={<AIFeatures />} />
        <Route path="/for-clinics" element={<ClinicOnboarding />} />
        <Route path="/partner-with-us" element={<ClinicOnboarding />} />
        <Route path="/for-investors" element={<InvestorForm />} />
        <Route path="/for-patients" element={<PatientWaitlist />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support" element={<Support />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <svg className="h-8 w-8 text-blue-400 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span className="text-xl font-bold">DoctrNow</span>
              </div>
              <p className="text-gray-400">AI-powered teleconsultation platform for the UAE</p>
              <div className="mt-4 text-gray-400 text-sm" data-testid="footer-company-info">
                <p className="font-semibold text-white">DoctrNow Healthcare LLC-FZ</p>
                <p>Dubai, United Arab Emirates</p>
                <p>Email: <a href="mailto:admin@doctrnow.com" className="hover:text-white transition-colors">admin@doctrnow.com</a></p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => setCurrentPage('home')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => setCurrentPage('ai-features')} className="hover:text-white transition-colors">AI Features</button></li>
                <li><button onClick={() => setCurrentPage('for-clinics')} className="hover:text-white transition-colors">For Clinics</button></li>
                <li><button onClick={() => setCurrentPage('for-investors')} className="hover:text-white transition-colors">For Investors</button></li>
                <li><button onClick={() => setCurrentPage('about-us')} className="hover:text-white transition-colors" data-testid="footer-about-link">About Us</button></li>
                <li><button onClick={() => setCurrentPage('privacy-policy')} className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</button></li>
                <li><button onClick={() => setCurrentPage('terms-and-conditions')} className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</button></li>
                <li><button onClick={() => setCurrentPage('support')} className="hover:text-white transition-colors" data-testid="footer-support-link">Support</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li>admin@doctrnow.com</li>
                <li>Dubai, United Arab Emirates</li>
                <li>Coming Soon</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Launch Cities</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Dubai</li>
                <li>Abu Dhabi</li>
                <li>Sharjah</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 DoctrNow. All rights reserved. | AI-powered healthcare for the UAE</p>
            <p className="mt-2" data-testid="footer-legal-copyright">&copy; 2026 DoctrNow Healthcare LLC-FZ. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Main App Component with router
function App() {
  return (
    <BrowserRouter>
      <Layout />
    </BrowserRouter>
  );
}

export default App;